import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// Get Permissions
export const getPermissions = createAsyncThunk(
  "Permissions/getPermissions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);

      const url = `${BASE_URL}/permissions${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get permissions!";
      return rejectWithValue(message);
    }
  },
);

// Get Permission Details
export const getPermissionDetails = createAsyncThunk(
  "Permissions/getPermissionDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/permissions/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get permission details!";
      return rejectWithValue(message);
    }
  },
);

// Create Permission
export const createPermission = createAsyncThunk(
  "Permissions/createPermission",
  async (permissionData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/permissions`,
        permissionData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to create permission!";
      return rejectWithValue(message);
    }
  },
);

// Update Permission
export const updatePermission = createAsyncThunk(
  "Permissions/updatePermission",
  async ({ id, permissionData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.put(
        `${BASE_URL}/permissions/${id}`,
        permissionData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to update permission!";
      return rejectWithValue(message);
    }
  },
);

// Delete Permission
export const deletePermission = createAsyncThunk(
  "Permissions/deletePermission",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(`${BASE_URL}/permissions/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete permission!";
      return rejectWithValue(message);
    }
  },
);
